import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLangContext } from '../../../providers/LangProvider/LangProviderContext';
import {
    CALC_DEFAULTS,
    CALC_LIMITS,
    calculateIncome,
    formatLandingUsd,
    parseCalcInput,
} from '../landingCalculator';

const LandingCalculatorStyled = styled.section``;
const EMPTY = '—';

function LandingCalculator({ onOpenIncomeHelp }) {
    const { getLangPath, locale } = useLangContext();
    const [lots, setLots] = useState(CALC_DEFAULTS.lots);
    const [subIncome, setSubIncome] = useState(CALC_DEFAULTS.sub);
    const lotsValue = parseCalcInput(lots, CALC_LIMITS.lots);
    const subValue = parseCalcInput(subIncome, CALC_LIMITS.sub);
    const valid = lotsValue !== null && subValue !== null;
    const result = valid ? calculateIncome(lotsValue, subValue) : null;
    const money = useMemo(() => (amount) => formatLandingUsd(amount, locale || 'en'), [locale]);

    const reset = () => {
        setLots(CALC_DEFAULTS.lots);
        setSubIncome(CALC_DEFAULTS.sub);
    };

    return (
        <LandingCalculatorStyled className="block comparison-section" aria-labelledby="sub-affiliate-section-label">
            <div className="wrap">
                <div className="calculator" id="income-calculator">
                    <div className="calc-layout">
                        <form id="income-inputs" noValidate onSubmit={(event) => event.preventDefault()}>
                            <div className="calc-heading">
                                <h2>{getLangPath('landing.calculator.title')}</h2>
                            </div>
                            <div className="calc-fields">
                                <label htmlFor="calc-lots">
                                    {getLangPath('landing.calculator.volumeLabel')}
                                    {' '}
                                    <span>{getLangPath('landing.calculator.volumeHint')}</span>
                                    <input
                                        id="calc-lots"
                                        type="number"
                                        min={CALC_LIMITS.lots.min}
                                        max={CALC_LIMITS.lots.max}
                                        step="0.01"
                                        value={lots}
                                        inputMode="decimal"
                                        onChange={(event) => setLots(event.target.value)}
                                    />
                                </label>
                                <label htmlFor="calc-sub">
                                    {getLangPath('landing.calculator.subLabel')}
                                    {' '}
                                    <span>{getLangPath('landing.calculator.subHint')}</span>
                                    <input
                                        id="calc-sub"
                                        type="number"
                                        min={CALC_LIMITS.sub.min}
                                        max={CALC_LIMITS.sub.max}
                                        step="0.01"
                                        value={subIncome}
                                        inputMode="decimal"
                                        onChange={(event) => setSubIncome(event.target.value)}
                                    />
                                </label>
                            </div>
                            <p className="calc-auto">{getLangPath('landing.calculator.auto')}</p>
                            <p className="calc-error" id="calc-error" role="alert" hidden={valid}>
                                {getLangPath('landing.calculator.error')}
                            </p>
                            <button className="calc-reset" type="button" onClick={reset}>
                                {getLangPath('landing.calculator.reset')}
                            </button>
                        </form>
                        <div className="calc-results" aria-live="polite" aria-atomic="true">
                            <div className={`calc-total${result && result.total >= 1000000 ? ' large-payout' : ''}`}>
                                <div className="calc-total-heading">
                                    <span>
                                        <b>Jetvix</b>
                                        {' '}
                                        {getLangPath('landing.calculator.totalSuffix')}
                                    </span>
                                </div>
                                <strong id="calc-total">{result ? money(result.total) : EMPTY}</strong>
                            </div>
                            <dl>
                                <div>
                                    <dt>{getLangPath('landing.calculator.rebate')}</dt>
                                    <dd id="calc-rebate">{result ? money(result.rebate) : EMPTY}</dd>
                                </div>
                                <div>
                                    <dt>{getLangPath('landing.calculator.override')}</dt>
                                    <dd id="calc-override">{result ? money(result.override) : EMPTY}</dd>
                                </div>
                                <div>
                                    <dt>{getLangPath('landing.calculator.bonus')}</dt>
                                    <dd id="calc-volume-bonus">{result ? money(result.bonus) : EMPTY}</dd>
                                </div>
                            </dl>
                            <div className="calc-other">
                                <span>
                                    <span className="broker-label">
                                        {getLangPath('landing.calculator.other')}
                                        <button
                                            type="button"
                                            className="income-help-button"
                                            id="open-income-help"
                                            aria-label={getLangPath('landing.calculator.helpAria')}
                                            aria-haspopup="dialog"
                                            aria-controls="income-help"
                                            onClick={onOpenIncomeHelp}
                                        >
                                            i
                                        </button>
                                    </span>
                                    <small>{getLangPath('landing.calculator.otherHint')}</small>
                                </span>
                                <strong id="calc-other">{result ? money(result.other) : EMPTY}</strong>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </LandingCalculatorStyled>
    );
}

export default LandingCalculator;
